import {
    ApplicationRef,
    ComponentFactoryResolver,
    ComponentRef,
    EmbeddedViewRef,
    Injectable,
    Injector,
    TemplateRef
} from '@angular/core';

import { ModalRef } from './modal-ref';
import { ModalWrapperComponent } from './modal-wrapper.component';

/**
 * @deprecated use Material modal
 */
@Injectable( {
    providedIn: 'root',
} )
export class ModalService {
    private modalWrapperRef: ComponentRef<ModalWrapperComponent>;
    private ariaHiddenElements = new Map<Element, string | null>();

    constructor(
        private componentFactoryResolver: ComponentFactoryResolver,
        private injector: Injector,
        private applicationRef: ApplicationRef
    ) { }

    public open ( modalContent: TemplateRef<any> ): ModalRef {
        // get overlay ref
        this.modalWrapperRef = this.appendModalWrapperToBody();
        this.modalWrapperRef.instance.childComponent = modalContent;
        // remove modal after close event
        const modalRef = new ModalRef();
        // remove modal when close method is called
        const sub = modalRef.afterClosed$.subscribe( () => {
            this.removeModalWrapper();
            sub.unsubscribe();
        } );
        this.modalWrapperRef.instance.onClose$.subscribe( () => this.removeModalWrapper() );

        this.hideNonModalFromAssistiveTechnology();

        return modalRef;
    }

    private appendModalWrapperToBody (): ComponentRef<ModalWrapperComponent> {
        // create an instance of the modal wrapper component
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory( ModalWrapperComponent );
        const componentRef = componentFactory.create( this.injector );
        // get root element of wrapper and attach it to the HTML body
        this.applicationRef.attachView( componentRef.hostView );
        const domElem = ( componentRef.hostView as EmbeddedViewRef<any> ).rootNodes[ 0 ] as HTMLElement;
        document.body.appendChild( domElem );

        return componentRef;
    }

    private removeModalWrapper () {
        this.unHideNonModalFromAssistiveTechnology();
        this.applicationRef.detachView( this.modalWrapperRef.hostView );
        this.modalWrapperRef.destroy();
    }

    private hideNonModalFromAssistiveTechnology () {
        const modalWrapperContainer = this.modalWrapperRef.instance.eRef.nativeElement;
        // Ensure that the modal wrapper container is attached to the DOM.
        if ( modalWrapperContainer.parentElement ) {
            const siblings = modalWrapperContainer.parentElement.children;

            for ( let i = siblings.length - 1; i > -1; i-- ) {
                const sibling = siblings[ i ];

                if ( sibling !== modalWrapperContainer &&
                    sibling.nodeName !== 'SCRIPT' &&
                    sibling.nodeName !== 'STYLE' &&
                    !sibling.hasAttribute( 'aria-live' ) ) {
                    // store previous value of an element if it already has aria-hidden
                    this.ariaHiddenElements.set( sibling, sibling.getAttribute( 'aria-hidden' ) );
                    sibling.setAttribute( 'aria-hidden', 'true' );
                }
            }
        }
    }

    private unHideNonModalFromAssistiveTechnology () {
        // if element had a previous value for aria-hidden we retrieve it from our store otherwise
        this.ariaHiddenElements.forEach( ( previousValue, element ) => {
            if ( previousValue ) {
                element.setAttribute( 'aria-hidden', previousValue );
            } else {
                element.removeAttribute( 'aria-hidden' );
            }
        } );

        this.ariaHiddenElements.clear();
    }
}
